import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MOCK_EVALUATORS } from "./mockSelectionData";

interface Props {
  scores: number[];           // length 3, one per evaluator
  color?: "blue" | "green" | "purple" | "amber";
}

const DOT_COLORS: Record<NonNullable<Props["color"]>, string> = {
  blue:   "#3b82f6",
  green:  "#16a34a",
  purple: "#9333ea",
  amber:  "#d97706",
};

const WIDTH = 60;
const HEIGHT = 16;
const Y = 8;
const R = 4;
const PAD = R + 1;

function variance(vals: number[]): number {
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  return vals.reduce((s, v) => s + (v - mean) ** 2, 0) / vals.length;
}

export function DotPlotCell({ scores, color = "blue" }: Props) {
  const fill = DOT_COLORS[color];
  const v = variance(scores);

  // Map score 1-5 to x position within [PAD, WIDTH-PAD]
  const xPos = (score: number) =>
    PAD + ((score - 1) / 4) * (WIDTH - 2 * PAD);

  const tooltipText = MOCK_EVALUATORS.map(
    (ev: string, i: number) => `${ev}: ${scores[i] ?? "—"}`
  ).join(" · ");

  const varianceLabel =
    v <= 0.5 ? "Agreement" : v <= 1.5 ? "Some disagreement" : "High disagreement";

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <svg
            width={WIDTH}
            height={HEIGHT}
            className="block"
            aria-label={tooltipText}
          >
            {/* Track line */}
            <line
              x1={PAD}
              y1={Y}
              x2={WIDTH - PAD}
              y2={Y}
              stroke="#e5e7eb"
              strokeWidth={2}
              strokeLinecap="round"
            />
            {/* Dots — rendered back-to-front so index 0 is on top */}
            {[...scores].reverse().map((score, ri) => {
              const i = scores.length - 1 - ri;
              return (
                <circle
                  key={i}
                  cx={xPos(score)}
                  cy={Y}
                  r={R}
                  fill={fill}
                  fillOpacity={0.75 + i * 0.1}
                  stroke="white"
                  strokeWidth={1}
                />
              );
            })}
          </svg>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <p className="font-medium">{tooltipText}</p>
          <p className="text-gray-400 mt-0.5">{varianceLabel} (σ²={v.toFixed(2)})</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
